<template>
  <b-modal :id="`CustomerContactManagementModal`" :ref="`CustomerContactManagementModal`" size="xl" scrollable>
    <template #modal-header>
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="modal-title mb-0">{{ title }}</h5>
        <div class="d-flex align-items-center">
          <b-button variant="secondary" size="sm" class="mr-3" @click="addNewContact">Add Contact</b-button>
          <button type="button" class="close ml-2" @click="checkChanges()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    </template>

    <b-table
      class="align-middle border"
      striped
      no-border-collapse
      :items="localContacts"
      :fields="editingFields"
      show-empty
      empty-text=""
    >
      <template #cell(firstName)="{ item }">
        <b-input v-model="item.firstName" />
      </template>

      <template #cell(lastName)="{ item }">
        <b-input v-model="item.lastName" />
      </template>

      <template #cell(phoneNumber)="{ item }">
        <b-input :value="formatPhoneNumber(item.phoneNumber)" @input="updatePhoneNumber(item, $event)" />
      </template>
      <template #cell(emailAddress)="{ item }">
        <b-input v-model="item.emailAddress" />
      </template>
      <template #cell(title)="{ item }">
        <b-input v-model="item.title" />
      </template>
      <template #cell(comment)="{ item, index }">
        <span v-if="item.comment !== undefined" :id="`comment-${index}`" class="text-primary" style="cursor: pointer">
          Edit
        </span>
        <b-popover
          v-if="item.comment !== undefined"
          :target="`comment-${index}`"
          triggers="hover focus"
          placement="left"
          :title="`Comments for ${item.firstName ? item.firstName : ''} ${item.lastName ? item.lastName : ''}`"
        >
          <b-form-textarea v-model="item.comment" rows="8" placeholder="Enter comment..."></b-form-textarea>
        </b-popover>
      </template>
      <template #cell(inactive)="{ item, index }">
        <div class="d-flex">
          <v-select
            v-model="item.inactive"
            :options="statusOptions"
            :reduce="option => option.value"
            label="label"
            :clearable="false"
          ></v-select>
          <b-button v-if="!item.contactId" class="ml-2" size="xsm" @click="localContacts.splice(index, 1)">
            <font-awesome-icon icon="trash-alt" />
          </b-button>
        </div>
      </template>
    </b-table>
    <template #modal-footer>
      <b-button size="sm" variant="primary" class="mr-2" @click="save">Save Contacts</b-button>
      <b-button size="sm" @click="checkChanges">Close</b-button>
    </template>
    <unsaved-changes-modal
      ref="UnsavedChangesModal"
      :has-changes="checkChanges"
      stay-on-page
      continue-btn-text="continue editing Contacts"
    ></unsaved-changes-modal>
  </b-modal>
</template>
<script>
import vSelect from 'vue-select';
import UnsavedChangesModal from '@/shared/components/UnsavedChangesModal';
import SuccessService from '../../../shared/services/SuccessService';
import ErrorService from '../../../shared/services/ErrorService';
import Cloner from '@/shared/helpers/cloner';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { CustomerGetters, CustomerMutations, CustomerActions } from '@/shared/store/customer/types';
import { ServiceOrderActions, ServiceOrderGetters } from '@/shared/store/service-order/types';
import { UnitGetters } from '@/shared/store/unit/types';
export default {
  name: 'CustomerContactManagementModal',
  components: {
    'unsaved-changes-modal': UnsavedChangesModal,
    'v-select': vSelect
  },
  props: {
    title: {
      type: String,
      default: 'Customer Contacts'
    },
    customerId: {
      type: String,
      required: true
    },
    isRepairContact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      statusOptions: [
        { label: 'Active', value: false },
        { label: 'Inactive', value: true }
      ],
      localContacts: []
    };
  },
  computed: {
    ...mapGetters({
      customerContacts: CustomerGetters.GET_CUSTOMER_CONTACTS_LIST,
      unit: UnitGetters.GET_UNIT,
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER
    }),
    editingFields() {
      const sharedOptions = { sortable: true };
      return [
        { key: 'firstName', label: 'First Name', ...sharedOptions },
        { key: 'lastName', label: 'Last Name', ...sharedOptions },
        { key: 'phoneNumber', label: 'Phone Number', ...sharedOptions },
        { key: 'emailAddress', label: 'Email Address', ...sharedOptions },
        { key: 'title', label: 'Title', ...sharedOptions },
        { key: 'comment', label: 'Comments', sortable: false, thStyle: 'width: 40px', tdClass: 'align-middle' },
        { key: 'inactive', label: 'Status', tdClass: 'align-middle', thStyle: 'width: 40px', sortable: false }
      ];
    }
  },
  async created() {
    await this[CustomerActions.FETCH_CUSTOMER_CONTACTS_LIST]({
      customerId: this.customerId,
      isEPay: false
    });
    this.localContacts = Cloner.deepClone(this.customerContacts);
  },
  methods: {
    ...mapMutations([CustomerMutations.SET_TO_EXISTING_CUSTOMER, CustomerMutations.SET_CUSTOMER_SEARCH_RESULTS]),
    ...mapActions([
      CustomerActions.FETCH_CUSTOMER_CONTACTS_LIST,
      CustomerActions.UPDATE_CUSTOMER_CONTACTS_LIST,
      ServiceOrderActions.FETCH_REPAIR_CONTACT
    ]),
    show() {
      this.localContacts = Cloner.deepClone(this.customerContacts);
      this.$bvModal.show(`CustomerContactManagementModal`);
    },
    checkChanges() {
      if (JSON.stringify(this.localContacts) !== JSON.stringify(this.customerContacts)) {
        this.$refs.UnsavedChangesModal.show(this, () => {
          this.hide();
        });
      } else {
        this.hide();
      }
    },
    hide() {
      if (this.localContacts.length > 0) {
        this.localContacts = [];
      }
      this.$bvModal.hide('CustomerContactManagementModal');
    },
    statusClass(status) {
      if (status === 'ACTIVE') {
        return '';
      }
      return 'status-warning';
    },
    addNewContact() {
      this.localContacts.push({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        emailAddress: '',
        title: '',
        inactive: false
      });
    },
    async save() {
      try {
        this.localContacts.forEach(contact => {
          if (!(contact.phoneNumber || contact.emailAddress) || !contact.lastName || !contact.firstName) {
            throw new Error('Contacts must have an email or phone number, and must have a first and last name.');
          }
        });
        await this[CustomerActions.UPDATE_CUSTOMER_CONTACTS_LIST]({
          contacts: this.localContacts,
          customerId: this.customerId,
          isRepairContact: this.isRepairContact
        });
        if (this.isRepairContact && this.serviceOrder?.repairContact?.contactId) {
          await this[ServiceOrderActions.FETCH_REPAIR_CONTACT]({
            unitId: this.unitId,
            serviceOrderId: this.serviceOrderId,
            customerId: this.serviceOrder.customer.customerId,
            repairContactId: this.serviceOrder.repairContact?.contactId
          });
        }
        SuccessService.createSuccessToast(this.$root, `Contacts saved successfully.`);
        this.hide();
      } catch (error) {
        let errorMessage = 'Failed to save contacts.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        ErrorService.createErrorToast(this, errorMessage);
      }
    },
    updatePhoneNumber(item, value) {
      // Store only the numeric digits
      const cleaned = ('' + value).replace(/\D/g, '');
      item.phoneNumber = cleaned;
    },
    formatPhoneNumber(value) {
      if (!value) return '';

      // Remove all non-numeric characters
      const cleaned = ('' + value).replace(/\D/g, '');

      // Format based on length
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }

      // Partial formatting for incomplete numbers
      if (cleaned.length > 6) {
        return '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3, 6) + '-' + cleaned.slice(6, 10);
      } else if (cleaned.length > 3) {
        return '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3, 6);
      } else if (cleaned.length > 0) {
        return '(' + cleaned.slice(0, 3);
      }

      return cleaned;
    }
  }
};
</script>
<style>
#CustomerContactManagementModal .modal-xl {
  max-width: 85vw;
  min-height: 40vh;
}
</style>
